import React, { createContext, useContext, useEffect, useState } from 'react';
import { colorTokens, typographyTokens, spacingTokens, radiusTokens, shadowTokens, motionTokens, getTokenValue } from './tokens';

interface ThemeContextType {
  tokens: {
    color: typeof colorTokens;
    typography: typeof typographyTokens;
    spacing: typeof spacingTokens;
    radius: typeof radiusTokens;
    shadow: typeof shadowTokens;
    motion: typeof motionTokens;
  };
  workspaceId: string | null;
  setWorkspaceId: (workspaceId: string | null) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode; workspaceId?: string | null }> = ({ 
  children, 
  workspaceId = null 
}) => {
  const [currentWorkspaceId, setWorkspaceId] = useState<string | null>(workspaceId);
  
  // Update CSS custom properties when workspaceId changes
  useEffect(() => {
    updateCSSVariables(currentWorkspaceId);
  }, [currentWorkspaceId]);
  
  const updateCSSVariables = (workspaceId: string | null) => {
    const root = document.documentElement;
    
    // Clear existing variables
    root.style.removeProperty('--color-neutral-50');
    root.style.removeProperty('--color-neutral-100');
    // ... (we'll set them all below)
    
    // Set color tokens
    Object.entries(colorTokens).forEach(([category, values]) => {
      Object.entries(values).forEach(([key, value]) => {
        const actualValue = getTokenValue(colorTokens, `${category}.${key}`, workspaceId);
        if (actualValue) {
          root.style.setProperty(`--color-${category}-${key}`, actualValue);
        }
      });
    });
    
    // Set semantic colors
    Object.entries({
      success: colorTokens.success,
      warning: colorTokens.warning,
      error: colorTokens.error,
      info: colorTokens.info
    }).forEach(([category, value]) => {
      const actualValue = getTokenValue(value, '500', workspaceId);
      if (actualValue) {
        root.style.setProperty(`--color-${category}-500`, actualValue);
      }
    });
    
    // Set typography tokens
    Object.entries(typographyTokens.fontSize).forEach(([key, value]) => {
      const actualValue = getTokenValue(typographyTokens.fontSize, key, workspaceId);
      if (actualValue) {
        root.style.setProperty(`--font-size-${key}`, actualValue);
      }
    });
    
    Object.entries(typographyTokens.fontWeight).forEach(([key, value]) => {
      const actualValue = getTokenValue(typographyTokens.fontWeight, key, workspaceId);
      if (actualValue !== null && actualValue !== undefined) {
        root.style.setProperty(`--font-weight-${key}`, actualValue.toString());
      }
    });
    
    Object.entries(typographyTokens.lineHeight).forEach(([key, value]) => {
      const actualValue = getTokenValue(typographyTokens.lineHeight, key, workspaceId);
      if (actualValue !== null && actualValue !== undefined) {
        root.style.setProperty(`--line-height-${key}`, actualValue.toString());
      }
    });
    
    // Set spacing tokens
    Object.entries(spacingTokens).forEach(([key, value]) => {
      const actualValue = getTokenValue(spacingTokens, key, workspaceId);
      if (actualValue) {
        root.style.setProperty(`--spacing-${key}`, actualValue);
      }
    });
    
    // Set radius tokens
    Object.entries(radiusTokens).forEach(([key, value]) => {
      const actualValue = getTokenValue(radiusTokens, key, workspaceId);
      if (actualValue) {
        root.style.setProperty(`--radius-${key}`, actualValue);
      }
    });
    
    // Set shadow tokens
    Object.entries(shadowTokens).forEach(([key, value]) => {
      const actualValue = getTokenValue(shadowTokens, key, workspaceId);
      if (actualValue) {
        root.style.setProperty(`--shadow-${key}`, actualValue);
      }
    });
    
    // Set motion tokens
    Object.entries(motionTokens.duration).forEach(([key, value]) => {
      const actualValue = getTokenValue(motionTokens.duration, key, workspaceId);
      if (actualValue) {
        root.style.setProperty(`--motion-duration-${key}`, actualValue);
      }
    });
    
    Object.entries(motionTokens.easing).forEach(([key, value]) => {
      const actualValue = getTokenValue(motionTokens.easing, key, workspaceId);
      if (actualValue) {
        root.style.setProperty(`--motion-easing-${key}`, actualValue);
      }
    });
  };
  
  const themeValue = {
    tokens: {
      color: colorTokens,
      typography: typographyTokens,
      spacing: spacingTokens,
      radius: radiusTokens,
      shadow: shadowTokens,
      motion: motionTokens
    },
    workspaceId: currentWorkspaceId,
    setWorkspaceId: setWorkspaceId
  };
  
  return React.createElement(ThemeContext.Provider, { value: themeValue }, children);
};

const defaultThemeValue: ThemeContextType = {
  tokens: {
    color: colorTokens,
    typography: typographyTokens,
    spacing: spacingTokens,
    radius: radiusTokens,
    shadow: shadowTokens,
    motion: motionTokens
  },
  workspaceId: null,
  setWorkspaceId: () => {}
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    return defaultThemeValue;
  }
  return context;
};